/**
 * Client for re-running the auth-letter extraction agent on an already-uploaded
 * letter (orange-icon / NewLetterUploaded flow).
 *
 * Hits the same `extract-auth-letter-upload` endpoint as the test harness, but
 * with `task_name=agent_rerun` so the backend persists the freshly extracted
 * values into the order's auth-letter records.
 * @module authLetterAgentRerunService
 */
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";

export interface AuthLetterAgentRerunParams {
  /** The auth letter file (downloaded from Firebase Storage). */
  file: File;
  payerName: string;
  orderId: string;
  orgId: string;
  /** J-codes to persist; the backend requires all of them to be found in the letter. */
  jCodes: string[];
  medicineNames: string[];
}

/**
 * Re-runs the auth-letter agent for an already-uploaded letter and persists the
 * extracted details.
 *
 * @input params - Letter file, payer, order, org, J-codes, and treatment-plan drug names
 * @throws {Error} When the user is not authenticated or the endpoint returns a non-OK response
 */
export const runAuthLetterAgentRerun = async (
  params: AuthLetterAgentRerunParams,
): Promise<void> => {
  const token = await getBearerToken();
  if (!token) throw new Error("Not authenticated — please log in again.");
  const form = new FormData();
  form.append("file", params.file);
  form.append("payer_name", params.payerName);
  form.append("order_id", params.orderId);
  form.append("org_id", params.orgId);
  form.append(
    "medicine_names_from_treatment_plan",
    JSON.stringify(params.medicineNames),
  );
  form.append("task_name", "agent_rerun");
  form.append("j_codes", JSON.stringify(params.jCodes));
  const res = await fetch(API_ENDPOINTS.EXTRACT_AUTH_LETTER_UPLOAD, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok)
    throw new Error(`Auth letter agent rerun failed (${res.status}): ${await res.text()}`);
};